import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Space {
  id: string
  name: string
  type: string
  owner: string
  pages_count: number
  members_count: number
}

interface Page {
  id: string
  space_name: string
  title: string
  author: string
  status: string
  updated_date: string
  views: number
  likes: number
}

export default function KnowledgePage() {
  const [spaces, setSpaces] = useState<Space[]>([])
  const [pages, setPages] = useState<Page[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [spaceRes, pageRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/knowledge/spaces'),
      fetch(API_BASE + '/knowledge/pages'),
      fetch(API_BASE + '/knowledge/statistics')
    ])
    if (spaceRes.ok) setSpaces((await spaceRes.json()).spaces || [])
    if (pageRes.ok) setPages((await pageRes.json()).pages || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800'
      case 'draft': return 'bg-yellow-100 text-yellow-800'
      case 'review': return 'bg-blue-100 text-blue-800'
      case 'archived': return 'bg-gray-100 text-gray-600'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Knowledge Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Create Page</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Spaces</div>
              <div className="text-2xl font-bold">{statistics.total_spaces}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Pages</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_pages}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Published</div>
              <div className="text-2xl font-bold text-green-600">{statistics.published_pages}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Views</div>
              <div className="text-2xl font-bold">{statistics.total_views?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Updated 30d</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.pages_updated_30_days}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Pages</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Page</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Space</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stats</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pages.slice(0, 12).map(page => (
                  <tr key={page.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{page.title}</div>
                      <div className="text-xs text-gray-500">By {page.author} • {page.updated_date}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm">{page.space_name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{page.views} views</div>
                      <div className="text-xs text-blue-600">{page.likes} likes</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(page.status)}`}>{page.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Spaces</h3>
            <div className="space-y-3">
              {spaces.map(space => (
                <div key={space.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{space.name}</span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">{space.type}</span>
                  </div>
                  <div className="text-xs text-gray-500">Owner: {space.owner}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{space.pages_count} pages</span>
                    <span className="text-xs text-gray-600">{space.members_count} members</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
